import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  User, Edit2, Bell, CreditCard, Shield, ChevronLeft, Save, Check, X,
  AlertTriangle, Trophy, Flame, Star, Settings, LogOut, Globe, Briefcase, MapPin
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Super admin emails that can change profession/country
const SUPER_ADMIN_EMAILS = ['santaliestralimited@gmail.com'];

const ProfilePage = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [editingNickname, setEditingNickname] = useState(false);
  const [newNickname, setNewNickname] = useState('');
  const [showInLeaderboard, setShowInLeaderboard] = useState(true);
  const [savingNickname, setSavingNickname] = useState(false);
  const [savingAlerts, setSavingAlerts] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancellingSubscription, setCancellingSubscription] = useState(false);

  const token = localStorage.getItem('session_token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/profile/`, { headers });
      setProfile(response.data);
      setNewNickname(response.data.nickname || '');
      setShowInLeaderboard(response.data.show_in_leaderboard || false);
    } catch (error) {
      toast.error('Error al cargar perfil');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNickname = async () => {
    if (!newNickname.trim()) {
      toast.error('El nickname no puede estar vacío');
      return;
    }
    
    setSavingNickname(true);
    try {
      await axios.post(`${API}/profile/nickname`, {
        nickname: newNickname.trim(),
        show_in_leaderboard: showInLeaderboard
      }, { headers });
      
      toast.success('Nickname guardado');
      setEditingNickname(false);
      loadProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar nickname');
    } finally {
      setSavingNickname(false);
    }
  };

  const handleRevokeNickname = async () => {
    if (!window.confirm('¿Seguro que quieres eliminar tu nickname? Ya no aparecerás en los rankings públicos.')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/profile/nickname`, { headers });
      toast.success('Nickname eliminado');
      loadProfile();
    } catch (error) {
      toast.error('Error al eliminar nickname');
    }
  };

  const handleToggleAlerts = async (accept) => {
    setSavingAlerts(true);
    try {
      const endpoint = accept ? 'accept' : 'revoke';
      await axios.post(`${API}/profile/alerts/${endpoint}`, {}, { headers });
      toast.success(accept ? 'Alertas activadas' : 'Alertas desactivadas');
      loadProfile();
    } catch (error) {
      toast.error('Error al actualizar preferencias');
    } finally {
      setSavingAlerts(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancellingSubscription(true);
    try {
      const response = await axios.post(`${API}/profile/subscription/cancel`, {
        reason: cancelReason,
        feedback: ''
      }, { headers });
      
      toast.success('Suscripción cancelada');
      setShowCancelModal(false);
      loadProfile();
      
      // Show details
      toast.info(response.data.details, { duration: 8000 });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cancelar suscripción');
    } finally {
      setCancellingSubscription(false);
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      await axios.post(`${API}/profile/subscription/reactivate`, {}, { headers });
      toast.success('Suscripción reactivada');
      loadProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al reactivar');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('session_token');
    navigate('/auth');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <button 
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
            >
              <ChevronLeft className="w-5 h-5" />
              <span>Volver al Dashboard</span>
            </button>
            <Button variant="ghost" size="sm" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              Salir
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          <h1 className="text-3xl font-bold text-slate-900">Mi Perfil</h1>

          {/* User Info Card */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                <span className="text-2xl font-bold text-white">
                  {profile?.name?.charAt(0)?.toUpperCase() || 'U'}
                </span>
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900">{profile?.name}</h2>
                <p className="text-slate-500">{profile?.email}</p>
              </div>
            </div>

            <div className="grid sm:grid-cols-3 gap-4">
              <div className="bg-slate-50 rounded-xl p-4">
                <div className="flex items-center gap-2 text-amber-600 mb-1">
                  <Flame className="w-5 h-5" />
                  <span className="font-medium">Racha</span>
                </div>
                <p className="text-2xl font-bold text-slate-900">
                  {profile?.gamification?.streak_days || 0} días
                </p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4">
                <div className="flex items-center gap-2 text-purple-600 mb-1">
                  <Star className="w-5 h-5" />
                  <span className="font-medium">XP</span>
                </div>
                <p className="text-2xl font-bold text-slate-900">
                  {profile?.gamification?.xp || 0}
                </p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4">
                <div className="flex items-center gap-2 text-indigo-600 mb-1">
                  <Trophy className="w-5 h-5" />
                  <span className="font-medium">Liga</span>
                </div>
                <p className="text-2xl font-bold text-slate-900 capitalize">
                  {profile?.gamification?.league || 'Bronce'}
                </p>
              </div>
            </div>
          </div>

          {/* Nickname Section */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                <Trophy className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900">Nombre Público (Gamificación)</h3>
                <p className="text-sm text-slate-500">Este nombre aparecerá en los rankings y competiciones</p>
              </div>
            </div>

            {!editingNickname ? (
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                <div>
                  <p className="font-medium text-slate-900">
                    {profile?.nickname || 'Sin nickname configurado'}
                  </p>
                  {profile?.show_in_leaderboard && profile?.nickname && (
                    <p className="text-sm text-emerald-600">✓ Visible en rankings</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setEditingNickname(true)}>
                    <Edit2 className="w-4 h-4 mr-1" />
                    {profile?.nickname ? 'Cambiar' : 'Configurar'}
                  </Button>
                  {profile?.nickname && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-rose-600"
                      onClick={handleRevokeNickname}
                    >
                      Eliminar
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">
                    Tu nickname (3-20 caracteres)
                  </label>
                  <Input
                    value={newNickname}
                    onChange={(e) => setNewNickname(e.target.value)}
                    placeholder="Ej: ElMejorOpositor"
                    maxLength={20}
                  />
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showInLeaderboard}
                    onChange={(e) => setShowInLeaderboard(e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm text-slate-600">Mostrar en rankings públicos</span>
                </label>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setEditingNickname(false);
                      setNewNickname(profile?.nickname || '');
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button 
                    onClick={handleSaveNickname}
                    disabled={savingNickname}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    {savingNickname ? 'Guardando...' : 'Guardar Nickname'}
                  </Button>
                </div>
              </div>
            )}

            <p className="text-xs text-slate-500 mt-4">
              💡 Puedes revocar tu nickname en cualquier momento. Al hacerlo, dejarás de aparecer en los rankings públicos.
            </p>
          </div>

          {/* Alerts Section */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <Bell className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900">Alertas de Inactividad</h3>
                <p className="text-sm text-slate-500">Te recordamos practicar si llevas tiempo sin entrar</p>
              </div>
            </div>

            <div className="p-4 bg-slate-50 rounded-xl">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-900">
                    {profile?.alert_preferences?.accept_inactivity_alerts 
                      ? '✅ Alertas activadas' 
                      : '❌ Alertas desactivadas'}
                  </p>
                  <p className="text-sm text-slate-500">
                    {profile?.alert_preferences?.accept_inactivity_alerts 
                      ? 'Recibirás recordatorios a los 3, 7, 10 y 14 días de inactividad'
                      : 'No recibirás recordatorios de práctica'}
                  </p>
                </div>
                <Button 
                  variant={profile?.alert_preferences?.accept_inactivity_alerts ? "outline" : "default"}
                  onClick={() => handleToggleAlerts(!profile?.alert_preferences?.accept_inactivity_alerts)}
                  disabled={savingAlerts}
                  className={!profile?.alert_preferences?.accept_inactivity_alerts ? "bg-amber-500 hover:bg-amber-600 text-white" : ""}
                >
                  {savingAlerts ? 'Guardando...' : 
                   profile?.alert_preferences?.accept_inactivity_alerts ? 'Desactivar' : 'Activar'}
                </Button>
              </div>
            </div>

            <p className="text-xs text-slate-500 mt-4">
              🔔 Las alertas te ayudan a mantener el hábito de estudio. Puedes desactivarlas cuando quieras.
            </p>
          </div>

          {/* Subscription Section */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900">Suscripción</h3>
                <p className="text-sm text-slate-500">Gestiona tu plan de suscripción</p>
              </div>
            </div>

            <div className="p-4 bg-slate-50 rounded-xl">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="font-medium text-slate-900 capitalize">
                    Plan {profile?.subscription?.plan || 'Free'}
                  </p>
                  <p className="text-sm text-slate-500">
                    Estado: <span className={`font-medium ${
                      profile?.subscription?.status === 'active' ? 'text-emerald-600' :
                      profile?.subscription?.status === 'cancelling' ? 'text-amber-600' : 'text-slate-600'
                    }`}>
                      {profile?.subscription?.status === 'active' ? 'Activo' :
                       profile?.subscription?.status === 'cancelling' ? 'Cancelándose' : profile?.subscription?.status}
                    </span>
                  </p>
                </div>
                {profile?.subscription?.plan !== 'free' && !profile?.subscription?.is_cancelled && (
                  <Button 
                    variant="outline" 
                    className="text-rose-600 border-rose-200 hover:bg-rose-50"
                    onClick={() => setShowCancelModal(true)}
                  >
                    Cancelar Suscripción
                  </Button>
                )}
                {profile?.subscription?.is_cancelled && (
                  <Button 
                    onClick={handleReactivateSubscription}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    Reactivar
                  </Button>
                )}
              </div>

              {profile?.subscription?.is_cancelled && profile?.subscription?.ends_at && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-sm text-amber-800">
                    <AlertTriangle className="w-4 h-4 inline mr-1" />
                    Tu suscripción finalizará el <strong>
                      {new Date(profile.subscription.ends_at).toLocaleDateString('es-ES')}
                    </strong>. 
                    Podrás seguir usando todas las funciones hasta esa fecha.
                  </p>
                </div>
              )}
            </div>

            <p className="text-xs text-slate-500 mt-4">
              💳 Al cancelar, tu suscripción permanecerá activa hasta el final del periodo pagado.
            </p>
          </div>

          {/* Study Configuration - For Super Admins */}
          {SUPER_ADMIN_EMAILS.includes(profile?.email) && (
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-2xl border-2 border-purple-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-purple-600 flex items-center justify-center">
                  <Settings className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-purple-900">⚡ Modo Administrador</h3>
                  <p className="text-sm text-purple-700">Cambiar país/profesión para testing</p>
                </div>
              </div>

              <div className="space-y-4">
                {/* Current Selection */}
                <div className="bg-white/70 rounded-xl p-4">
                  <p className="text-sm font-medium text-slate-700 mb-2">Selección actual:</p>
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg border border-slate-200">
                      <Globe className="w-4 h-4 text-indigo-600" />
                      <span className="text-sm font-medium">{profile?.selected_country || 'Sin país'}</span>
                    </div>
                    <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg border border-slate-200">
                      <Briefcase className="w-4 h-4 text-purple-600" />
                      <span className="text-sm font-medium">{profile?.profession_name || profile?.selected_profession || 'Sin profesión'}</span>
                    </div>
                    <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg border border-slate-200">
                      <MapPin className="w-4 h-4 text-emerald-600" />
                      <span className="text-sm font-medium">{profile?.selected_region || 'Sin región'}</span>
                    </div>
                  </div>
                </div>

                {/* Change Buttons */}
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    onClick={() => navigate('/select-country')}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  >
                    <Globe className="w-4 h-4 mr-2" />
                    Cambiar País
                  </Button>
                  <Button
                    onClick={() => navigate('/select-profession')}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    <Briefcase className="w-4 h-4 mr-2" />
                    Cambiar Profesión
                  </Button>
                </div>

                <p className="text-xs text-purple-600">
                  🔧 Como administrador, puedes cambiar libremente entre países y profesiones para verificar que todo funcione correctamente.
                </p>
              </div>
            </div>
          )}
        </motion.div>
      </div>

      {/* Cancel Subscription Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl max-w-md w-full p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-rose-100 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-rose-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900">¿Cancelar suscripción?</h2>
            </div>

            <p className="text-slate-600 mb-4">
              Tu suscripción permanecerá activa hasta el final del mes actual. Después, perderás acceso a las funciones premium.
            </p>

            <div className="mb-4">
              <label className="text-sm font-medium text-slate-700 block mb-1">
                ¿Por qué te vas? (opcional)
              </label>
              <select 
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg"
              >
                <option value="">Selecciona un motivo...</option>
                <option value="price">Demasiado caro</option>
                <option value="not_using">No lo uso lo suficiente</option>
                <option value="passed">Ya aprobé mi oposición</option>
                <option value="found_alternative">Encontré otra alternativa</option>
                <option value="technical">Problemas técnicos</option>
                <option value="other">Otro motivo</option>
              </select>
            </div>

            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={() => setShowCancelModal(false)}
                className="flex-1"
              >
                Volver
              </Button>
              <Button 
                onClick={handleCancelSubscription}
                disabled={cancellingSubscription}
                className="flex-1 bg-rose-600 hover:bg-rose-700 text-white"
              >
                {cancellingSubscription ? 'Cancelando...' : 'Confirmar Cancelación'}
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
